import type { YutaiMaster } from "@yct/core";

/**
 * Static market data (R2 via /data/v1). Each file is cached in memory and in
 * localStorage, keyed by the timestamp in meta.json so a changed file is
 * refetched and an unchanged one is served from cache.
 */

const base = import.meta.env.VITE_API_ORIGIN ?? "";

export interface StockRow {
  code: string;
  name: string;
  market: string;
  sector: string;
  isLoanable: boolean;
  unit: number;
}

export interface YutaiIndexEntry {
  code: string;
  recordMonths: number[];
  recordDayType: string;
  categoryTags: string[];
  minShares: number;
  hasLongTerm: boolean;
  summary: string;
}

export interface RestrictionEntry {
  kind: string;
  level: "stop" | "caution";
  from: string;
  to: string | null;
}

export interface RecordDateEntry {
  code: string;
  recordDate: string;
  lastTradingDay: string;
  exDate: string;
}

let metaPromise: Promise<Record<string, string>> | null = null;
const memory = new Map<string, unknown>();

async function meta(): Promise<Record<string, string>> {
  if (!metaPromise) {
    metaPromise = fetch(`${base}/data/v1/meta.json`)
      .then((r) => (r.ok ? (r.json() as Promise<Record<string, string>>) : {}))
      .catch(() => ({}));
  }
  return metaPromise;
}

async function load<T>(path: string, fallback: T): Promise<T> {
  const stamp = (await meta())[path] ?? "";
  const key = `yct.data.${path}`;
  const cached = memory.get(key) as { stamp: string; value: T } | undefined;
  if (cached && cached.stamp === stamp) return cached.value;
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw) as { stamp: string; value: T };
      if (parsed.stamp === stamp) {
        memory.set(key, parsed);
        return parsed.value;
      }
    }
  } catch {
    // ignore storage errors
  }
  const res = await fetch(`${base}/data/v1/${path}`);
  if (!res.ok) return fallback;
  const value = (await res.json()) as T;
  const entry = { stamp, value };
  memory.set(key, entry);
  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // quota or private mode
  }
  return value;
}

export const data = {
  stocks: () => load<{ stocks: StockRow[] }>("stocks.json", { stocks: [] }).then((d) => d.stocks),
  yutaiIndex: () => load<{ entries: YutaiIndexEntry[] }>("yutai/index.json", { entries: [] }).then((d) => d.entries),
  yutai: (code: string) => load<YutaiMaster | null>(`yutai/${code}.json`, null),
  prices: () => load<{ updatedAt?: string; prices: Record<string, { date: string; close: number }> }>("prices/latest.json", { prices: {} }),
  restrictions: () => load<{ restrictions: Record<string, RestrictionEntry[]> }>("restrictions/latest.json", { restrictions: {} }).then((d) => d.restrictions),
  gyakuhibu: (code: string) => load<{ history: Array<{ date: string; ratePerShare: number; days: number }> }>(`gyakuhibu/${code}.json`, { history: [] }).then((d) => d.history),
  recordDates: (month: string) => load<{ events: RecordDateEntry[] }>(`record-dates/${month}.json`, { events: [] }).then((d) => d.events),
};

export const restrictionLabel: Record<string, string> = {
  jsf_loan_caution: "貸株注意喚起（日証金）",
  jsf_loan_restricted: "貸株申込停止（日証金）",
  exchange_margin_increase: "増担保規制（取引所）",
  exchange_daily_disclosure: "日々公表銘柄（取引所）",
};

export const categoryLabel: Record<string, string> = {
  food: "食事券",
  quo: "QUOカード",
  catalog: "カタログ",
  own_product: "自社製品",
  discount: "割引券",
  cash_voucher: "金券",
  other: "その他",
};

export const recordDayLabel: Record<string, string> = {
  month_end: "月末",
  day_15: "15日",
  day_20: "20日",
  other: "その他",
};

/** Worst restriction level for a stock, or null. */
export function restrictionLevel(entries: RestrictionEntry[] | undefined): "stop" | "caution" | null {
  if (!entries || entries.length === 0) return null;
  return entries.some((e) => e.level === "stop") ? "stop" : "caution";
}
